import { useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Drawer,
  FileButton,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminErrorMessage, adminGet, adminSend } from "../../../api.ts";
import type { AnalyticsCredential } from "../../../api.ts";
import { notifyAdminError, notifyAdminSuccess } from "../../../lib/admin-notify.ts";
import { CredentialDeleteConfirm } from "./CredentialDeleteConfirm.tsx";
import { PlatformTutorialBody, platformTutorialTitle } from "./PlatformTutorials.tsx";

interface GoogleForm {
  saJson: string;
  operatingCustomerId: string;
  loginCustomerId: string;
}

interface CredentialListResponse {
  items: AnalyticsCredential[];
}

interface TestResponse {
  ok: boolean;
  error?: string;
  note?: string;
  saEmail?: string | null;
  operatingCustomerId?: string | null;
}

/**
 * Google 凭据多卡片管理：
 * - 同 bot 可配多个 google 凭据（每个 customer_id 一条），UNIQUE (bot_id, platform, external_id)
 *   = (bot_id, 'google', operating_customer_id)
 * - 走拆出的独立子路由 /analytics/:botId/credentials/google
 * - conversion_action_id 与 credential_id 都下沉 ad 级配置（见 AdsTab）
 */
function GoogleCredRow(props: {
  readonly botId: string;
  readonly cred: AnalyticsCredential;
  readonly test: {
    readonly isPending: boolean;
    readonly variables: string | undefined;
    readonly mutate: (id: string) => void;
  };
  readonly onRequestEdit: (cred: AnalyticsCredential) => void;
  readonly onRequestDelete: (cred: AnalyticsCredential) => void;
}) {
  const { botId, cred, test, onRequestEdit, onRequestDelete } = props;
  const qc = useQueryClient();
  const [remarkOpen, setRemarkOpen] = useState(false);
  const [remarkDraft, setRemarkDraft] = useState(cred.remark ?? "");
  const saveRemark = useMutation({
    mutationFn: (remark: string) =>
      adminSend("PATCH", `/analytics/${botId}/credentials/${cred.id}`, {
        remark: remark.trim() === "" ? null : remark.trim().slice(0, 64),
      }),
    onSuccess: () => {
      notifyAdminSuccess("备注已保存");
      setRemarkOpen(false);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-credentials"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  return (
    <>
      <Card withBorder padding="sm" radius="sm">
        <Group justify="space-between" wrap="nowrap">
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs">
              <Code>{cred.googleSaEmail ?? "(SA email 未知)"}</Code>
              {cred.remark !== null && cred.remark !== "" && (
                <Badge color="yellow" size="xs" variant="light">
                  {cred.remark}
                </Badge>
              )}
              {cred.isEnabled ? (
                <Badge color="green" size="xs">
                  已启用
                </Badge>
              ) : (
                <Badge color="gray" size="xs">
                  已禁用
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              Operating: <Code>{cred.googleOperatingCustomerId ?? "?"}</Code> · Login:{" "}
              <Code>{cred.googleLoginCustomerId ?? "?"}</Code>
            </Text>
          </Stack>
          <Group gap="xs">
            <Button size="xs" variant="outline" onClick={() => onRequestEdit(cred)}>
              编辑
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() => {
                setRemarkDraft(cred.remark ?? "");
                setRemarkOpen(true);
              }}
            >
              备注
            </Button>
            <Button
              size="xs"
              variant="light"
              loading={test.isPending && test.variables === cred.id}
              onClick={() => test.mutate(cred.id)}
            >
              测试鉴权
            </Button>
            <Button size="xs" variant="outline" color="red" onClick={() => onRequestDelete(cred)}>
              删除
            </Button>
          </Group>
        </Group>
      </Card>
      <Drawer
        title="Google 投放备注"
        opened={remarkOpen}
        onClose={() => setRemarkOpen(false)}
        size="sm"
        position="right"
      >
        <Stack gap="sm">
          <Text size="xs" c="dimmed">
            Customer {cred.googleOperatingCustomerId ?? "?"}
          </Text>
          <TextInput
            label="备注（投放名称）"
            placeholder="例如：印度主账号 MCC"
            maxLength={64}
            value={remarkDraft}
            onChange={(e) => setRemarkDraft(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRemarkOpen(false)}>
              取消
            </Button>
            <Button loading={saveRemark.isPending} onClick={() => saveRemark.mutate(remarkDraft)}>
              保存
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </>
  );
}

export function GoogleCredentialCard(props: {
  readonly botId: string;
  /** 由父页统一出教程 Drawer 时传入；不传则本组件自带教程 Drawer */
  readonly onOpenTutorial?: () => void;
  /** 隐藏外层说明卡，仅列表+操作（嵌入「平台凭据」页） */
  readonly embedded?: boolean;
}) {
  const { botId, onOpenTutorial, embedded = false } = props;
  const qc = useQueryClient();
  const resetFileRef = useRef<() => void>(null);
  const [formOpen, setFormOpen] = useState(false);
  /** null = 新建；非 null = 编辑该凭据行 */
  const [editTarget, setEditTarget] = useState<AnalyticsCredential | null>(null);
  const [pendingSaPreview, setPendingSaPreview] = useState<string | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AnalyticsCredential | null>(null);
  const [deleteStep, setDeleteStep] = useState<1 | 2>(1);

  const openTutorial = () => {
    if (onOpenTutorial !== undefined) onOpenTutorial();
    else setGuideOpen(true);
  };

  const form = useForm<GoogleForm>({
    initialValues: { saJson: "", operatingCustomerId: "", loginCustomerId: "" },
    validate: {
      saJson: (v) => {
        // 新建必传 SA；编辑可不传（保留原密钥）
        if (editTarget !== null) return null;
        return v.trim() === "" ? "请上传 Service Account JSON" : null;
      },
      operatingCustomerId: (v) =>
        v.trim() === "" || !/^\d{10}$/.test(v.trim())
          ? "Operating Customer ID 必须是 10 位数字（去横线）"
          : null,
      loginCustomerId: (v) =>
        v.trim() === "" || !/^\d{10}$/.test(v.trim()) ? "Login Customer ID 必须是 10 位数字" : null,
    },
  });

  const list = useQuery({
    queryKey: ["bot", botId, "analytics-credentials"],
    queryFn: () => adminGet<CredentialListResponse>(`/analytics/${botId}/credentials`),
    refetchInterval: 60_000,
  });

  const googleCreds = (list.data?.items ?? []).filter((c) => c.platform === "google");
  const hasNoGoogle = googleCreds.length === 0;

  const save = useMutation({
    mutationFn: (v: GoogleForm) => {
      const operatingCustomerId = v.operatingCustomerId.trim();
      const loginCustomerId = v.loginCustomerId.trim();
      if (editTarget !== null) {
        const body: Record<string, unknown> = {
          operatingCustomerId,
          loginCustomerId,
        };
        if (v.saJson.trim() !== "") body["saJson"] = v.saJson;
        return adminSend("PATCH", `/analytics/${botId}/credentials/google/${editTarget.id}`, body);
      }
      return adminSend("PUT", `/analytics/${botId}/credentials/google`, {
        saJson: v.saJson,
        operatingCustomerId,
        loginCustomerId,
      });
    },
    onSuccess: () => {
      notifyAdminSuccess(
        editTarget !== null ? "已更新 Google 凭据" : "已保存 Google 凭据",
        "保存成功",
      );
      form.reset();
      setPendingSaPreview(null);
      setEditTarget(null);
      resetFileRef.current?.();
      setFormOpen(false);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-credentials"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  // 与 FB/TikTok 共用 POST .../credentials/:credId/test（credId = 行 UUID）
  const test = useMutation({
    mutationFn: (credId: string) =>
      adminSend<TestResponse>("POST", `/analytics/${botId}/credentials/${credId}/test`, {}),
    onSuccess: (resp) => {
      if (resp.ok) {
        const who =
          resp.note ?? (resp.saEmail !== null && resp.saEmail !== undefined ? resp.saEmail : null);
        notifyAdminSuccess(
          who !== null ? `Google 鉴权通过（${who}）` : "Google 鉴权通过",
          "测试通过",
        );
      } else {
        notifications.show({
          color: "red",
          title: "测试未通过",
          message: resp.error ?? "Google 鉴权失败",
          autoClose: 12_000,
        });
      }
    },
    onError: (e) => notifyAdminError(e, "测试失败"),
  });

  const deleteCred = useMutation({
    mutationFn: (credId: string) =>
      adminSend("DELETE", `/analytics/${botId}/credentials/${credId}`),
    onSuccess: () => {
      notifyAdminSuccess("Google 凭据已删除");
      setDeleteTarget(null);
      setDeleteStep(1);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-credentials"] });
    },
    onError: (e) => notifyAdminError(e, "删除失败"),
  });

  const handleSaJsonFile = async (file: File | null) => {
    if (file === null) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as { client_email?: unknown; private_key?: unknown };
      if (typeof parsed.client_email !== "string" || parsed.client_email === "") {
        notifications.show({ color: "red", message: "JSON 缺 client_email 字段" });
        return;
      }
      if (typeof parsed.private_key !== "string" || parsed.private_key === "") {
        notifications.show({ color: "red", message: "JSON 缺 private_key 字段" });
        return;
      }
      form.setFieldValue("saJson", text);
      setPendingSaPreview(parsed.client_email);
    } catch {
      notifications.show({ color: "red", message: "JSON 解析失败，请确认文件格式" });
    }
  };

  const openAddForm = () => {
    form.reset();
    setEditTarget(null);
    setPendingSaPreview(null);
    resetFileRef.current?.();
    setFormOpen(true);
  };

  const openEditForm = (cred: AnalyticsCredential) => {
    form.setValues({
      saJson: "",
      operatingCustomerId: cred.googleOperatingCustomerId ?? "",
      loginCustomerId: cred.googleLoginCustomerId ?? "",
    });
    setEditTarget(cred);
    setPendingSaPreview(null);
    resetFileRef.current?.();
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditTarget(null);
    form.reset();
    setPendingSaPreview(null);
    resetFileRef.current?.();
  };

  if (list.isLoading) {
    return <Skeleton h={200} />;
  }

  return (
    <>
      <Card withBorder padding="md">
        <Stack gap="md">
          {!embedded ? (
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text fw={600}>Google Ads (Data Manager API)</Text>
                  <Badge color="yellow" size="sm">
                    {googleCreds.length} 条
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  SA JSON 鉴权，无 developer-token。ctId 在「广告列表」配置。
                </Text>
              </Stack>
              <Group gap="xs">
                <Button size="xs" variant="light" onClick={openTutorial}>
                  接入教程
                </Button>
                <Button size="xs" onClick={openAddForm}>
                  添加凭据
                </Button>
              </Group>
            </Group>
          ) : (
            <Group justify="flex-end" gap="xs">
              <Button size="xs" variant="light" onClick={openTutorial}>
                接入教程
              </Button>
              <Button size="xs" onClick={openAddForm}>
                添加 Google 凭据
              </Button>
            </Group>
          )}

          {hasNoGoogle ? (
            <Alert color="yellow" variant="light" title="尚未配置 Google 凭据">
              <Stack gap="xs">
                <Text size="sm">先看接入教程准备 SA JSON 与 Customer ID，再添加凭据。</Text>
                <Group gap="xs">
                  <Button size="xs" onClick={openAddForm}>
                    添加凭据
                  </Button>
                  <Button size="xs" variant="light" onClick={openTutorial}>
                    打开教程
                  </Button>
                </Group>
              </Stack>
            </Alert>
          ) : (
            <Stack gap="sm">
              {googleCreds.map((cred) => (
                <GoogleCredRow
                  key={cred.id}
                  botId={botId}
                  cred={cred}
                  test={test}
                  onRequestEdit={openEditForm}
                  onRequestDelete={(c) => {
                    setDeleteTarget(c);
                    setDeleteStep(1);
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Card>

      <Drawer
        opened={formOpen}
        onClose={closeForm}
        title={editTarget !== null ? "编辑 Google 凭据" : "添加 Google 凭据"}
        size="lg"
        position="right"
      >
        <form onSubmit={form.onSubmit((v) => save.mutate(v))}>
          <Stack gap="sm">
            <Alert color="blue" variant="light">
              <Text size="xs">
                SA JSON 来自 Google Cloud；Operating / Login 为 10 位去横线 Customer ID。ctId
                在「广告列表」填。
                {editTarget !== null ? " 编辑可不重传 JSON，保留原密钥。" : ""}
              </Text>
            </Alert>
            {save.isError ? (
              <Alert color="red" title="保存失败">
                <Text size="xs" className="admin-error-text">
                  {adminErrorMessage(save.error)}
                </Text>
              </Alert>
            ) : null}
            {editTarget !== null && (
              <Text size="xs" c="dimmed">
                当前 SA：<Code>{editTarget.googleSaEmail ?? "(未知)"}</Code>
              </Text>
            )}
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Service Account JSON {editTarget === null ? "*" : "（可选）"}
              </Text>
              <Group gap="sm" align="center">
                <FileButton
                  resetRef={resetFileRef}
                  accept="application/json,.json"
                  onChange={(f) => void handleSaJsonFile(f)}
                >
                  {(fbprops) => (
                    <Button variant="light" {...fbprops}>
                      {editTarget !== null ? "重新上传 SA JSON" : "上传 SA JSON"}
                    </Button>
                  )}
                </FileButton>
                <Text size="xs" c="dimmed" style={{ flex: 1, minWidth: 0 }}>
                  {pendingSaPreview !== null
                    ? `即将保存：${pendingSaPreview}`
                    : editTarget !== null
                      ? "未更换文件，保留原密钥"
                      : "尚未选择文件"}
                </Text>
              </Group>
              {typeof form.errors["saJson"] === "string" && (
                <Text size="xs" c="red">
                  {form.errors["saJson"]}
                </Text>
              )}
            </Stack>
            <TextInput
              label="Operating Customer ID"
              placeholder="10 位无横线"
              required
              {...form.getInputProps("operatingCustomerId")}
            />
            <TextInput
              label="Login Customer ID"
              placeholder="MCC 或同 Operating"
              required
              {...form.getInputProps("loginCustomerId")}
            />
            <Group justify="space-between" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  closeForm();
                  openTutorial();
                }}
              >
                看教程
              </Button>
              <Group gap="xs">
                <Button variant="default" onClick={closeForm}>
                  取消
                </Button>
                <Button type="submit" loading={save.isPending}>
                  {editTarget !== null ? "保存修改" : "保存"}
                </Button>
              </Group>
            </Group>
          </Stack>
        </form>
      </Drawer>

      {onOpenTutorial === undefined ? (
        <Drawer
          opened={guideOpen}
          onClose={() => setGuideOpen(false)}
          title={platformTutorialTitle("google")}
          size="xl"
          position="right"
        >
          <ScrollArea h="calc(100vh - 80px)" type="auto" offsetScrollbars>
            <PlatformTutorialBody platform="google" />
          </ScrollArea>
        </Drawer>
      ) : null}

      <CredentialDeleteConfirm
        opened={deleteTarget !== null}
        step={deleteStep}
        summary={
          deleteTarget !== null
            ? `google · ${deleteTarget.googleOperatingCustomerId ?? "?"} · ${deleteTarget.googleSaEmail ?? "?"}${deleteTarget.remark !== null && deleteTarget.remark !== "" ? ` · ${deleteTarget.remark}` : ""}`
            : ""
        }
        loading={deleteCred.isPending}
        onClose={() => {
          if (deleteCred.isPending) return;
          setDeleteTarget(null);
          setDeleteStep(1);
        }}
        onContinue={() => setDeleteStep(2)}
        onConfirmDelete={() => {
          if (deleteTarget === null) return;
          deleteCred.mutate(deleteTarget.id);
        }}
      />
    </>
  );
}
